import {
  Button,
  CustomRewardBalance,
  CustomRewardCurrency,
  FlexBox,
  Modal,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { CloseButton } from "../../../components/closeButton/CloseButton"
import { getScreenTimeConfigurations } from "../../../util/util"

interface RedemptionSuccessModalStoreProps {
  isOpen: boolean
  onOpen: ({
    rewardBalance,
    childName,
    quantity,
  }: {
    rewardBalance: CustomRewardBalance
    childName?: string
    quantity: number
  }) => void
  onClose: () => void
  rewardBalance: CustomRewardBalance | null
  childName?: string
  quantity: number
}

export const useRedemptionSuccessModalStore =
  create<RedemptionSuccessModalStoreProps>((set) => ({
    isOpen: false,
    onOpen: ({ rewardBalance, childName, quantity }) =>
      set({
        isOpen: true,
        rewardBalance,
        childName,
        quantity,
      }),
    onClose: () =>
      set({
        isOpen: false,
        rewardBalance: null,
        childName: "",
        quantity: 0,
      }),
    rewardBalance: null,
    childName: "",
    quantity: 0,
  }))

export const RedemptionSuccessModal = () => {
  const { isOpen, onClose, rewardBalance, childName, quantity } =
    useRedemptionSuccessModalStore()
  const { numLinkedDevices } = getScreenTimeConfigurations(
    rewardBalance?.reward || null,
    rewardBalance?.user_id || undefined
  )
  const { t } = useTranslation()
  const rewardEmoji = rewardBalance?.reward?.emoji
  const rewardTitle = rewardBalance?.reward?.title
  const isMinuteReward =
    rewardBalance?.reward?.currency === CustomRewardCurrency.MINUTE
  const rewardLabel = `${rewardEmoji} ${rewardTitle}`

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ zIndex: 1000, width: "min(100%, 450px)" }}
      animate
    >
      <FlexBox
        direction="column"
        align="left"
        justify="center"
        style={{ padding: `${SPACING.space4} ${SPACING.space6}` }}
        gap={SPACING.space4}
        fullWidth
      >
        <CloseButton onClick={onClose} />

        <FlexBox direction="column" gap={SPACING.space4}>
          <Typography variant="bodyBold">
            {numLinkedDevices > 0
              ? t("RSM_HEADER_APPROVED_READY", {
                  reward: rewardLabel,
                  name: childName ?? t("RSM_YOUR_CHILD_LOW"),
                })
              : isMinuteReward
                ? t("RSM_HEADER_APPROVED", { reward: rewardLabel })
                : t("RSM_HEADER_USED", { reward: rewardLabel })}
          </Typography>

          {numLinkedDevices > 0 && (
            <>
              <Typography variant="bodySmall">
                {quantity === 1
                  ? t("RSM_EXTRA_MINUTES_ONE", {
                      name: childName ?? t("RSM_YOUR_CHILD_CAP"),
                      count: quantity,
                    })
                  : t("RSM_EXTRA_MINUTES_OTHER", {
                      name: childName ?? t("RSM_YOUR_CHILD_CAP"),
                      count: quantity,
                    })}
              </Typography>
              <Typography variant="bodySmall">
                {t("RSM_ACTIVATE_INSTRUCTIONS", {
                  name: childName ?? t("RSM_YOUR_CHILD_CAP"),
                })}
              </Typography>
            </>
          )}

          {numLinkedDevices === 0 && (
            <Typography variant="bodySmall">
              {t("RSM_ALREADY_GAVE", {
                name: childName ?? t("RSM_YOUR_CHILD_LOW"),
                reward: rewardTitle,
              })}
            </Typography>
          )}
        </FlexBox>

        <Button
          buttonType="primary"
          text={t("RSM_OKAY")}
          onClick={onClose}
          fullWidth
        />
      </FlexBox>
    </Modal>
  )
}
